<template>
  <div class="workspace-page journal journal-reader">
    <router-link
      :to="backLink"
      class="product-back journal-back"
    >
      <GrowthIcon name="arrow" />{{ $t('blog.backToList') }}
    </router-link>
    <div
      v-if="loading"
      class="journal-post-loading"
      role="status"
    >
      <p>{{ $t('blog.loading') }}</p><span /><span /><div class="journal-feature-skeleton" />
    </div>
    <div
      v-else-if="error"
      class="product-empty journal-error"
      role="alert"
    >
      <GrowthIcon name="file" /><h1>{{ $t(notFound ? 'journal.not-found-title' : 'journal.post-error-title') }}</h1><p>{{ $t(notFound ? 'journal.not-found-copy' : 'journal.post-error-copy') }}</p><button
        v-if="!notFound"
        class="growth-button"
        @click="loadPost"
      >
        {{ $t('general.retry') }}
      </button><router-link
        v-else
        :to="backLink"
        class="growth-button"
      >
        {{ $t('journal.all-stories') }}
      </router-link>
    </div>
    <template v-else-if="post">
      <div
        class="journal-progress"
        role="progressbar"
        :aria-label="$t('journal.reading-progress')"
        :aria-valuenow="progress"
        aria-valuemin="0"
        aria-valuemax="100"
      >
        <span :style="{ width: `${progress}%` }" />
      </div>
      <header class="journal-post-heading">
        <router-link
          v-if="category"
          :to="{ path: '/blog', query: { topic: post.category.key, ...(locale === 'es' ? { lang: 'es' } : {}) } }"
          class="journal-topic-label"
        >
          {{ category }}
        </router-link><p
          v-else
          class="workspace-eyebrow"
        >
          {{ $t('journal.eyebrow') }}
        </p><h1>{{ title }}</h1><p
          v-if="description"
          class="journal-post-deck"
        >
          {{ description }}
        </p><div class="journal-byline">
          <span
            v-if="post.author?.name"
            class="journal-author"
          ><img
            v-if="authorImage && !authorImageFailed"
            :src="authorImage"
            alt=""
            @error="authorImageFailed = true"
          ><span>{{ post.author.name }} {{ post.author.last_name }}</span></span><time
            v-if="date"
            :datetime="post.publishing_date"
          >{{ date }}</time><span v-if="minutes"><GrowthIcon name="clock" />{{ $t('journal.read-time', { count: minutes }) }}</span>
        </div>
      </header>
      <BlogCover
        :image="post.image"
        :seed="post.key || post._id"
        :title="title"
        eager
        class="journal-post-cover"
      />
      <div class="journal-reading-layout">
        <aside class="journal-reading-rail">
          <details
            v-if="article.headings.length"
            class="journal-contents"
            :open="contentsOpen"
            @toggle="contentsOpen = $event.target.open"
          >
            <summary>{{ $t('journal.on-this-page') }}<GrowthIcon name="menu" /></summary><nav :aria-label="$t('journal.on-this-page')">
              <a
                v-for="heading in article.headings"
                :key="heading.id"
                :href="`#${heading.id}`"
                :class="{ 'is-subheading': heading.level === 3, active: activeHeading === heading.id }"
                :aria-current="activeHeading === heading.id ? 'location' : undefined"
                @click.prevent="goToHeading(heading.id)"
              >{{ heading.text }}</a>
            </nav>
          </details><div class="journal-share">
            <button
              class="product-text-button"
              :disabled="copying"
              @click="copyLink"
            >
              <GrowthIcon :name="copied ? 'check' : 'link'" />{{ $t(copied ? 'journal.link-copied' : 'journal.copy-link') }}
            </button><p
              v-if="copied"
              class="sr-only"
              role="status"
            >
              {{ $t('journal.link-copied') }}
            </p><label v-if="copyError">{{ $t('journal.copy-error') }}<input
              :value="canonical"
              readonly
              :aria-label="$t('journal.article-link')"
              @focus="$event.target.select()"
            ></label>
          </div>
        </aside>
        <div class="journal-reading-main">
          <p
            v-if="fallbackLanguage"
            class="journal-language-note"
          >
            {{ $t('journal.english-article') }}
          </p><article
            ref="articleBody"
            class="journal-prose"
            :lang="contentLanguage"
            :aria-label="title"
            v-html="article.html"
          /><p
            v-if="!article.html.trim()"
            class="journal-small-message"
          >
            {{ $t('journal.content-unavailable') }}
          </p><div class="journal-reading-end">
            <span>{{ $t('journal.end-note') }}</span><router-link
              :to="backLink"
              class="product-text-button"
            >
              {{ $t('journal.more-stories') }}<GrowthIcon name="arrow" />
            </router-link>
          </div>
        </div>
      </div>
      <BlogGrowthCard />
      <section
        v-if="relatedLoading || relatedError || related.length"
        class="journal-related"
        :aria-busy="relatedLoading"
      >
        <div class="journal-section-heading">
          <h2>{{ $t('journal.keep-reading') }}</h2><router-link
            :to="backLink"
            class="product-text-button"
          >
            {{ $t('journal.all-stories') }}<GrowthIcon name="arrow" />
          </router-link>
        </div><p
          v-if="relatedLoading"
          role="status"
        >
          {{ $t('journal.loading-stories') }}
        </p><div
          v-else-if="relatedError"
          class="product-message"
          role="alert"
        >
          <p>{{ $t('journal.related-error') }}</p><button @click="loadRelated">
            {{ $t('general.retry') }}
          </button>
        </div><div
          v-else
          class="journal-grid"
        >
          <BlogCard
            v-for="item in related"
            :key="item.key || item._id"
            :post="item"
            :context="backLink.query"
          />
        </div>
      </section>
      <AdSenseUnit
        v-if="!isPro"
        :ad-slot="AD_PLACEMENTS.article.slot"
        ad-format="horizontal"
        container-class="journal-ad"
      />
    </template>
  </div>
</template>
<script setup>
import { AD_PLACEMENTS } from '@/config/ads';
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/store/auth';
import { usePageSeo } from '@/composables/usePageSeo';
import { canonicalUrl, blogLocales, seoDate, contentModifiedDate } from '@/config/seoUrls';
import { SITE_URL, SITE_NAME, buildBreadcrumbSchema } from '@/config/seo';
import { blogText, blogCategory, blogDate, blogUrl, blogPath, blogPage, blogTopic, blogItems, readingMinutes, plainBlogText, prepareArticle } from '@/utils/blog';
import blogService from '@/services/blogService';
import BlogCover from '@/components/blog/BlogCover.vue';
import BlogCard from '@/components/blog/BlogCard.vue';
import BlogGrowthCard from '@/components/blog/BlogGrowthCard.vue';
import GrowthIcon from '@/components/growth/GrowthIcon.vue';
import AdSenseUnit from '@/components/ui/AdSenseUnit.vue';
const { t, locale } = useI18n(); const route = useRoute(); const auth = useAuthStore();
const isPro = computed(() => !!auth.currentUser?.subscription);
const post = ref(null); const loading = ref(true); const error = ref(false); const notFound = ref(false);
const related = ref([]); const relatedLoading = ref(false); const relatedError = ref(false);
const articleBody = ref(null); const progress = ref(0); const activeHeading = ref('');
const contentsOpen = ref(typeof window !== 'undefined' && window.innerWidth > 1000);
const copied = ref(false); const copying = ref(false); const copyError = ref(false); const authorImageFailed = ref(false);
const title = computed(() => blogText(post.value, 'title', contentLanguage.value));
const description = computed(() => plainBlogText(blogText(post.value, 'description', contentLanguage.value)));
const content = computed(() => blogText(post.value, 'content', locale.value));
const contentLanguage = computed(() => locale.value.startsWith('es') && post.value?.content_es?.trim() ? 'es' : 'en');
const fallbackLanguage = computed(() => locale.value.split('-')[0] !== contentLanguage.value);
const category = computed(() => blogCategory(post.value, locale.value));
const date = computed(() => blogDate(post.value?.publishing_date, locale.value));
const minutes = computed(() => readingMinutes(content.value));
const authorImage = computed(() => blogUrl(post.value?.author?.profile_image));
const article = computed(() => prepareArticle(content.value, title.value, t('journal.open-media')));
const canonical = computed(() => canonicalUrl(post.value ? blogPath(post.value) : route.path, contentLanguage.value));
const backLink = computed(() => ({ path: '/blog', query: { ...(locale.value === 'es' ? { lang: 'es' } : {}), ...(blogPage(route.query.page) > 1 ? { page: String(blogPage(route.query.page)) } : {}), ...(blogTopic(route.query.topic) ? { topic: blogTopic(route.query.topic) } : {}) } }));
let wideLayout = typeof window !== 'undefined' && window.innerWidth > 1000;
let request; let relatedRequest; let version = 0; let relatedVersion = 0; let alive = true; let frame; let resizeObserver;
async function loadPost() {
  const current = ++version; request?.abort(); relatedRequest?.abort(); relatedVersion++; request = new AbortController();
  loading.value = true; error.value = false; notFound.value = false; post.value = null; related.value = []; relatedError.value = false;
  progress.value = 0; activeHeading.value = ''; copied.value = false; copyError.value = false; authorImageFailed.value = false;
  try {
    const response = await blogService.get(route.params.id, { signal: request.signal });
    if (!alive || current !== version) return;
    if (!response || (!response.key && !response._id) || !blogText(response, 'title', locale.value)) throw new Error('Invalid post');
    post.value = response;
    loadRelated();
  } catch (failure) { if (alive && current === version) { error.value = true; notFound.value = failure?.response?.status === 404; } }
  finally { if (alive && current === version) loading.value = false; }
}
async function loadRelated() {
  if (!post.value) return;
  const current = ++relatedVersion; relatedRequest?.abort(); relatedRequest = new AbortController();
  const selected = post.value; relatedLoading.value = true; relatedError.value = false;
  try {
    const params = { page: 1, limit: 4, published: 1 }; const options = { signal: relatedRequest.signal }; let response;
    if (selected.category?.key) {
      try { response = await blogService.getByCategory(selected.category.key, params, options); }
      catch (failure) { if (failure?.response?.status !== 404) throw failure; response = await blogService.getAll(params, options); }
    } else response = await blogService.getAll(params, options);
    if (alive && current === relatedVersion) related.value = blogItems(response).items.filter(item => (item.key || item._id) !== (selected.key || selected._id)).slice(0, 3);
  } catch { if (alive && current === relatedVersion) relatedError.value = true; }
  finally { if (alive && current === relatedVersion) relatedLoading.value = false; }
}
async function copyLink() {
  if (copying.value) return;
  const current = version; copying.value = true; copyError.value = false;
  try { await navigator.clipboard.writeText(canonical.value); if (alive && current === version) copied.value = true; }
  catch { if (alive && current === version) { copyError.value = true; copied.value = false; } }
  finally { if (alive) copying.value = false; }
}
async function goToHeading(id) {
  if (window.innerWidth <= 1000) contentsOpen.value = false;
  // Section jumps stay local so they never wait for a session refresh.
  await nextTick();
  const heading = articleBody.value?.querySelector(`#${id}`);
  heading?.scrollIntoView({ block: 'start', behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' }); heading?.focus({ preventScroll: true });
  activeHeading.value = id;
}
function updateReading() {
  if (!articleBody.value) return;
  const rect = articleBody.value.getBoundingClientRect();
  const distance = Math.max(1, rect.height - (window.innerHeight - 120));
  progress.value = Math.max(0, Math.min(100, Math.round((120 - rect.top) / distance * 100)));
  const headings = [...articleBody.value.querySelectorAll('h2[id],h3[id]')];
  activeHeading.value = headings.filter(el => el.getBoundingClientRect().top <= 170).at(-1)?.id || headings[0]?.id || '';
}
function handleResize() {
  const wide = window.innerWidth > 1000;
  if (wide !== wideLayout) { wideLayout = wide; contentsOpen.value = wide; }
  scheduleReading();
}
function scheduleReading() { if (!frame) frame = requestAnimationFrame(() => { frame = null; updateReading(); }); }
watch(() => route.params.id, loadPost, { immediate: true });
watch(() => article.value.html, async () => {
  await nextTick(); if (!alive) return;
  resizeObserver?.disconnect();
  if (typeof ResizeObserver !== 'undefined' && articleBody.value) { resizeObserver = new ResizeObserver(scheduleReading); resizeObserver.observe(articleBody.value); }
  if (article.value.headings.some(heading => `#${heading.id}` === route.hash)) await goToHeading(route.hash.slice(1));
  scheduleReading();
}, { flush: 'post' });
onMounted(() => { window.addEventListener('scroll', scheduleReading, true); window.addEventListener('resize', handleResize); });
onBeforeUnmount(() => { alive = false; version++; relatedVersion++; request?.abort(); relatedRequest?.abort(); resizeObserver?.disconnect(); cancelAnimationFrame(frame); window.removeEventListener('scroll', scheduleReading, true); window.removeEventListener('resize', handleResize); });
const jsonLd = computed(() => [post.value ? { '@context': 'https://schema.org', '@type': 'BlogPosting', mainEntityOfPage: { '@type': 'WebPage', '@id': canonical.value }, headline: title.value, description: description.value, image: blogUrl(post.value.image) || undefined, datePublished: seoDate(post.value.publishing_date), dateModified: contentModifiedDate(post.value), author: post.value.author?.name ? { '@type': 'Person', name: `${post.value.author.name} ${post.value.author.last_name || ''}`.trim() } : undefined, publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL, logo: { '@type': 'ImageObject', url: `${SITE_URL}/og-image.jpg` } }, articleSection: category.value || undefined, inLanguage: contentLanguage.value } : null, buildBreadcrumbSchema([{ name: SITE_NAME, url: '/' }, { name: t('blog.title'), url: '/blog' }, ...(post.value ? [{ name: title.value, url: canonical.value }] : [])])].filter(Boolean));
usePageSeo({ title: computed(() => title.value || t(notFound.value ? 'journal.not-found-title' : 'blog.title')), description: computed(() => description.value || t('blog.subtitle')), ogType: 'article', ogImage: computed(() => blogUrl(post.value?.image) || undefined), canonical, noindex: notFound, jsonLd, locales: computed(() => post.value ? blogLocales(post.value) : []), contentLocale: contentLanguage, articleMeta: computed(() => ({ published_time: seoDate(post.value?.publishing_date), modified_time: contentModifiedDate(post.value) })) });
</script>
<style src="../styles/journal.css"></style>
